import { useUTCDate } from "../../../../../../../shared/infrastructure/ui/hooks/use-utc-date";
import {
  ArrowIcon,
  Input,
  MagnifyingGlassIcon,
} from "../../../../../../../shared/infrastructure";
import { LocationT, MarketScoresT } from "../../../../../domain";
import React, { useMemo } from "react";

type LocationProps = {
  isDedicated?: boolean;
  location: LocationT;
  score: number;
  zipCodeName: string;
};

const Location = ({
  isDedicated = false,
  location,
  score,
  zipCodeName,
}: LocationProps): React.ReactElement => {
  const { dateFromUTC } = useUTCDate();

  const label = useMemo(() => {
    if (score === 0) {
      return <span className="text-[#919191]">0.0</span>;
    }
    if (score > 0) {
      return (
        <span className="text-[#4CAF50]">+{Math.round(score * 100) / 100}</span>
      );
    }
    if (score < 0) {
      return (
        <span className="text-[#F44336]">{Math.round(score * 100) / 100}</span>
      );
    }
  }, [score]);

  return (
    <section>
      <section className="grid">
        <span className="text-right text-[12px] font-[700]">{label}</span>
        <span
          className="overflow-hidden text-ellipsis whitespace-nowrap text-[20px] font-[700] leading-[1em]"
          title={`${location.city}, ${
            location.state_code ?? location.state_id
          }`}
        >
          {location.city}, {location.state_code ?? location.state_id}
        </span>
      </section>
      {!isDedicated && (
        <section className="grid">
          <span className="text-[10px] text-[#919191]">
            {dateFromUTC(location.arrive_early_datetime).format(
              "MM/DD/YYYY HH:mm"
            )}
          </span>
          {location.arrive_late_datetime &&
            location.arrive_early_datetime !==
              location.arrive_late_datetime && (
              <span className="text-[10px] text-[#919191]">
                {dateFromUTC(location.arrive_late_datetime).format(
                  "MM/DD/YYYY HH:mm"
                )}
              </span>
            )}
        </section>
      )}
      {location.zipCode ?? location.zip_code ? (
        <section className="text-[10px] font-[700]">
          Zip Code: {location.zip_code ?? location.zipCode}
        </section>
      ) : (
        <section className="grid grid-flow-col items-baseline gap-[2px] text-[10px] font-[700]">
          <span>Zip Code:</span>
          <Input
            name={zipCodeName}
            placeholder="Zip Code"
            autoFocus={true}
            rootInputProps={{
              className: "outline-0 text-[10px]",
            }}
            classNames={{
              input: "p-0",
            }}
          />
          <button type="submit">
            <MagnifyingGlassIcon fill="#147EFB" />
          </button>
        </section>
      )}
    </section>
  );
};

type OrderLocationsProps = {
  isDedicated?: boolean;
  locationA: LocationT;
  locationB: LocationT;
  marketScores: MarketScoresT;
  distance: number | null;
  stops: number | null;
};

const OrderLocations = ({
  isDedicated = false,
  locationA,
  locationB,
  marketScores,
  distance,
  stops,
}: OrderLocationsProps): React.ReactElement => {
  return (
    <section className="grid grid-cols-[1fr_auto_1fr] items-center gap-[27px]">
      <Location
        isDedicated={isDedicated}
        location={locationA}
        score={marketScores.backhaul_month ?? 0}
        zipCodeName="origin_zip_code"
      />
      <section className="grid justify-items-center">
        <ArrowIcon width={20} height={20} className="-rotate-90" />
        {stops !== null && (
          <span className="text-[10px] text-[#FFBD2E]">{stops} stops</span>
        )}
        {distance !== null && (
          <span className="text-[10px] text-[#9E9E9E]">{distance} ml</span>
        )}
      </section>
      <Location
        isDedicated={isDedicated}
        location={locationB}
        score={marketScores.headhaul_month ?? 0}
        zipCodeName="destination_zip_code"
      />
    </section>
  );
};

export { OrderLocations, Location };
